import { Container, Graphics, Text } from 'pixi.js';
import type { ScenarioLine, ScenarioRuby } from '../app/types';
import {
  createTextLayoutDebugEntry,
  fitTextToBounds,
  type TextLayoutDebugEntry
} from './TextLayoutDebug';
import { UI_COLORS, UI_FONT_FAMILY, UI_TEXT_STYLES } from './Theme';

export interface TextBoxOptions {
  width?: number;
  height?: number;
  speaker?: string;
  text?: string;
  highlights?: string[];
  ruby?: ScenarioRuby[];
  compact?: boolean;
}

interface TextBoxTransition {
  elapsedMs: number;
  durationMs: number;
  fromAlpha: number;
  toAlpha: number;
  hideWhenComplete: boolean;
}

interface RubyRange {
  start: number;
  end: number;
  base: string;
  reading: string;
}

interface RubyLayoutToken {
  base: string;
  reading?: string;
}

const TEXT_BOX_TRANSITION_MS = 160;
const SPEAKER_TRANSITION_MS = 180;
const PROMPT_PULSE_MS = 720;
const MAX_HIGHLIGHT_COUNT = 4;
const HIGHLIGHT_GAP = 8;
const HIGHLIGHT_HEIGHT = 28;
const RUBY_FONT_SCALE = 0.38;
const RUBY_MIN_FONT_SIZE = 9;
const RUBY_GAP = 2;
const RUBY_LINE_GAP = 4;

export class TextBox extends Container {
  private readonly background = new Graphics();
  private readonly speakerPlate = new Graphics();
  private readonly speakerText = new Text({
    text: '',
    style: UI_TEXT_STYLES.textBoxSpeaker
  });
  private readonly bodyText = new Text({
    text: '',
    style: UI_TEXT_STYLES.textBoxBody
  });
  private readonly bodyRubyLayer = new Container();
  private readonly highlightLayer = new Container();
  private readonly promptIndicator = new Graphics();
  private readonly bodyMask = new Graphics();

  private boxWidth: number;
  private boxHeight: number;
  private compact: boolean;
  private highlights: string[] = [];
  private transition: TextBoxTransition | null = null;
  private speakerTransitionMs = 0;
  private promptElapsedMs = 0;
  private textLayoutReference = '';
  private rubyAnnotations: ScenarioRuby[] = [];
  private rubyRanges: RubyRange[] = [];
  private fittedBodyFontSize: number | null = null;
  private fittedBodyLineHeight: number | null = null;

  public constructor(options: TextBoxOptions = {}) {
    super();
    this.boxWidth = options.width ?? 1160;
    this.boxHeight = options.height ?? 190;
    this.compact = options.compact ?? false;

    this.addChild(this.background);
    this.addChild(this.speakerPlate);
    this.addChild(this.speakerText);
    this.addChild(this.highlightLayer);
    this.addChild(this.bodyText);
    this.addChild(this.bodyRubyLayer);
    this.addChild(this.promptIndicator);
    this.addChild(this.bodyMask);
    this.bodyText.mask = this.bodyMask;
    this.bodyRubyLayer.mask = this.bodyMask;

    this.setSpeaker(options.speaker ?? '');
    this.setHighlights(options.highlights ?? []);
    this.setRubyAnnotations(options.ruby ?? []);
    this.setText(options.text ?? '');
    this.draw();
  }

  public setLine(line: Pick<ScenarioLine, 'speaker' | 'text' | 'highlights' | 'ruby'>): void {
    this.setSpeaker(line.speaker ?? '');
    this.setHighlights(line.highlights ?? []);
    this.prepareTextLayout(line.text, line.ruby ?? []);
    this.setText(line.text);
    this.show();
  }

  public prepareTextLayout(text: string, ruby: readonly ScenarioRuby[] = []): void {
    this.textLayoutReference = text;
    this.setRubyAnnotations(ruby);
    this.recalculateBodyFit();
  }

  public setText(text: string): void {
    this.bodyText.text = text;
    if (!this.textLayoutReference) {
      this.recalculateBodyFit();
    }
    this.applyFittedBodyStyle();
    this.renderBodyText(text);
  }

  public setSpeaker(speaker: string): void {
    const changed = speaker !== this.speakerText.text;
    this.speakerText.text = speaker;
    this.speakerPlate.visible = speaker.length > 0;
    this.speakerText.visible = speaker.length > 0;
    if (changed && speaker.length > 0) {
      this.speakerTransitionMs = SPEAKER_TRANSITION_MS;
    }
    this.layout();
    this.drawHighlights();
  }

  public setHighlights(highlights: readonly string[]): void {
    const nextHighlights = Array.from(
      new Set(
        highlights.map((highlight) => highlight.trim()).filter((highlight) => highlight.length > 0)
      )
    ).slice(0, MAX_HIGHLIGHT_COUNT);

    if (arraysEqual(this.highlights, nextHighlights)) {
      return;
    }

    this.highlights = nextHighlights;
    this.drawHighlights();
  }

  public setPromptVisible(visible: boolean): void {
    this.promptIndicator.visible = visible;
    if (visible) {
      this.promptElapsedMs = 0;
    }
  }

  public setCompact(compact: boolean): void {
    this.compact = compact;
    this.draw();
  }

  public setSize(width: number, height: number): void {
    this.boxWidth = width;
    this.boxHeight = height;
    this.draw();
  }

  public getSize(): { width: number; height: number } {
    return {
      width: this.boxWidth,
      height: this.boxHeight
    };
  }

  public getTextLayoutDebugEntries(prefix = 'textBox'): TextLayoutDebugEntry[] {
    return [
      createTextLayoutDebugEntry(
        `${prefix}.speaker`,
        this.speakerText,
        this.getSpeakerPlateWidth() - 34,
        38
      ),
      createTextLayoutDebugEntry(
        `${prefix}.body`,
        this.bodyText,
        this.boxWidth - 84,
        this.getBodyHeight()
      )
    ];
  }

  public show(animated = true): void {
    this.visible = true;
    this.transition = animated
      ? {
          elapsedMs: 0,
          durationMs: TEXT_BOX_TRANSITION_MS,
          fromAlpha: this.alpha,
          toAlpha: 1,
          hideWhenComplete: false
        }
      : null;
    if (!animated) {
      this.alpha = 1;
    }
  }

  public hide(animated = true): void {
    if (animated) {
      this.transition = {
        elapsedMs: 0,
        durationMs: TEXT_BOX_TRANSITION_MS,
        fromAlpha: this.alpha,
        toAlpha: 0,
        hideWhenComplete: true
      };
      return;
    }

    this.alpha = 0;
    this.visible = false;
    this.transition = null;
  }

  public update(deltaMs: number): void {
    const safeDeltaMs = Math.max(0, deltaMs);
    this.updateTransition(safeDeltaMs);
    this.updateSpeakerTransition(safeDeltaMs);
    this.updatePromptPulse(safeDeltaMs);
  }

  private draw(): void {
    const radius = 8;
    this.background.clear();
    this.background
      .roundRect(0, 0, this.boxWidth, this.boxHeight, radius)
      .fill({ color: UI_COLORS.paperDark, alpha: 0.88 })
      .stroke({ color: UI_COLORS.gold, width: 2, alpha: 0.75 });
    this.background.rect(0, 0, 10, this.boxHeight).fill({ color: UI_COLORS.red, alpha: 0.9 });
    this.background
      .rect(24, this.boxHeight - 9, this.boxWidth - 48, 2)
      .fill({ color: UI_COLORS.gold, alpha: 0.38 });

    this.speakerPlate.clear();
    this.speakerPlate
      .roundRect(34, 18, this.getSpeakerPlateWidth(), 38, 6)
      .fill({ color: UI_COLORS.gold, alpha: 0.95 })
      .stroke({ color: UI_COLORS.black, width: 1, alpha: 0.7 });

    this.promptIndicator.clear();
    this.promptIndicator
      .moveTo(0, 0)
      .lineTo(18, 0)
      .lineTo(9, 11)
      .closePath()
      .fill({ color: UI_COLORS.gold, alpha: 0.88 });

    this.layout();
    this.drawHighlights();
  }

  private layout(): void {
    const bodyX = 42;
    const bodyY = this.getBodyY();
    const bodyWidth = this.boxWidth - 84;

    this.speakerText.position.set(52, 23);
    this.fitSpeakerText();
    this.bodyText.position.set(bodyX, bodyY);
    this.bodyRubyLayer.position.set(bodyX, bodyY);
    this.bodyText.style.wordWrapWidth = bodyWidth;
    this.recalculateBodyFit();
    this.applyFittedBodyStyle();
    this.renderBodyText(this.bodyText.text);
    this.promptIndicator.position.set(this.boxWidth - 54, this.boxHeight - 34);
    this.drawBodyMask();
  }

  private fitSpeakerText(): void {
    const maxWidth = this.getSpeakerPlateWidth() - 34;
    fitTextToBounds(this.speakerText, {
      maxWidth,
      maxHeight: 38,
      baseFontSize: this.compact ? 18 : 21,
      minFontSize: this.compact ? 13 : 15,
      baseLineHeight: this.compact ? 22 : 25,
      minLineHeight: this.compact ? 16 : 18,
      wordWrap: true,
      breakWords: true
    });
  }

  private applyBodyTextBaseStyle(): void {
    this.bodyText.style.fontSize = this.compact ? 25 : 29;
    this.bodyText.style.lineHeight = this.compact ? 36 : 42;
  }

  private applyFittedBodyStyle(): void {
    if (this.fittedBodyFontSize === null || this.fittedBodyLineHeight === null) {
      this.applyBodyTextBaseStyle();
      this.renderBodyText(this.bodyText.text);
      return;
    }

    this.bodyText.style.fontSize = this.fittedBodyFontSize;
    this.bodyText.style.lineHeight = this.fittedBodyLineHeight;
    this.renderBodyText(this.bodyText.text);
  }

  private recalculateBodyFit(): void {
    const currentText = this.bodyText.text;
    const measurementText = this.textLayoutReference || currentText;

    this.applyBodyTextBaseStyle();
    this.bodyText.text = measurementText;
    this.fitBodyText();
    this.fittedBodyFontSize = Number(this.bodyText.style.fontSize);
    this.fittedBodyLineHeight = Number(this.bodyText.style.lineHeight);
    this.bodyText.text = currentText;
  }

  private setRubyAnnotations(ruby: readonly ScenarioRuby[]): void {
    this.rubyAnnotations = ruby.filter(
      (entry) => entry.base.trim().length > 0 && entry.reading.trim().length > 0
    );
    this.rubyRanges = this.resolveRubyRanges();
  }

  private renderBodyText(text: string): void {
    if (this.rubyRanges.length === 0) {
      this.bodyText.visible = true;
      this.bodyRubyLayer.visible = false;
      destroyChildren(this.bodyRubyLayer);
      return;
    }

    this.bodyText.visible = false;
    this.bodyRubyLayer.visible = true;
    this.renderRubyText(text);
  }

  private renderRubyText(visibleText: string): void {
    destroyChildren(this.bodyRubyLayer);

    if (visibleText.length === 0) {
      return;
    }

    const fontSize = Number(this.bodyText.style.fontSize);
    const lineHeight = Number(this.bodyText.style.lineHeight);
    const rubyFontSize = Math.max(RUBY_MIN_FONT_SIZE, Math.round(fontSize * RUBY_FONT_SCALE));
    const baseY = rubyFontSize + RUBY_GAP;
    const rubyLineAdvance = this.getRubyLineAdvance(lineHeight, rubyFontSize);
    const bodyWidth = this.boxWidth - 84;
    const tokens = this.buildRubyTokens(visibleText.length);

    let x = 0;
    let y = 0;

    for (const token of tokens) {
      if (token.base === '\n') {
        x = 0;
        y += rubyLineAdvance;
        continue;
      }

      const baseText = this.createBodyFragment(token.base, fontSize, lineHeight);
      const rubyText = token.reading
        ? this.createRubyFragment(token.reading, rubyFontSize, rubyFontSize + 2)
        : null;
      const tokenWidth = Math.ceil(Math.max(baseText.width, rubyText?.width ?? 0));

      if (x > 0 && x + tokenWidth > bodyWidth) {
        x = 0;
        y += rubyLineAdvance;
      }

      const tokenContainer = new Container();
      if (rubyText) {
        rubyText.position.set(Math.round((tokenWidth - rubyText.width) / 2), 0);
        tokenContainer.addChild(rubyText);
      }
      baseText.position.set(Math.round((tokenWidth - baseText.width) / 2), baseY);
      tokenContainer.addChild(baseText);
      tokenContainer.position.set(Math.round(x), Math.round(y));
      this.bodyRubyLayer.addChild(tokenContainer);

      x += tokenWidth;
    }
  }

  private createBodyFragment(text: string, fontSize: number, lineHeight: number): Text {
    return new Text({
      text,
      style: {
        fill: UI_COLORS.ink,
        fontFamily: UI_FONT_FAMILY,
        fontSize,
        lineHeight,
        fontWeight: '500',
        wordWrap: false
      }
    });
  }

  private createRubyFragment(text: string, fontSize: number, lineHeight: number): Text {
    return new Text({
      text,
      style: {
        fill: UI_COLORS.gold,
        fontFamily: UI_FONT_FAMILY,
        fontSize,
        lineHeight,
        fontWeight: '700',
        wordWrap: false
      }
    });
  }

  private buildRubyTokens(visibleCharacterCount: number): RubyLayoutToken[] {
    const reference = this.textLayoutReference || this.bodyText.text;
    const tokens: RubyLayoutToken[] = [];
    let index = 0;

    while (index < visibleCharacterCount) {
      const range = this.rubyRanges.find((entry) => entry.start === index);
      if (range && range.end <= visibleCharacterCount) {
        tokens.push({
          base: reference.slice(range.start, range.end),
          reading: range.reading
        });
        index = range.end;
        continue;
      }

      tokens.push({ base: reference.slice(index, index + 1) });
      index += 1;
    }

    return tokens;
  }

  private resolveRubyRanges(): RubyRange[] {
    const reference = this.textLayoutReference;
    if (reference.length === 0 || this.rubyAnnotations.length === 0) {
      return [];
    }

    const ranges: RubyRange[] = [];

    for (const annotation of this.rubyAnnotations) {
      const start = findRubyBaseIndex(reference, annotation, ranges);
      if (start < 0) {
        continue;
      }

      ranges.push({
        start,
        end: start + annotation.base.length,
        base: annotation.base,
        reading: annotation.reading
      });
    }

    return ranges.sort((left, right) => left.start - right.start);
  }

  private fitBodyText(): void {
    const minFontSize = this.compact ? 20 : 21;
    const minLineHeight = this.compact ? 29 : 30;
    const bodyHeight = this.getBodyHeight();

    while (
      this.bodyText.text.length > 0 &&
      this.bodyText.height > bodyHeight &&
      Number(this.bodyText.style.fontSize) > minFontSize
    ) {
      const nextFontSize = Number(this.bodyText.style.fontSize) - 1;
      const nextLineHeight = Math.max(minLineHeight, Number(this.bodyText.style.lineHeight) - 2);
      this.bodyText.style.fontSize = nextFontSize;
      this.bodyText.style.lineHeight = nextLineHeight;
    }

    this.fitRubyText();
  }

  private fitRubyText(): void {
    if (this.rubyRanges.length === 0) {
      return;
    }

    const minFontSize = this.compact ? 18 : 19;
    const minLineHeight = this.compact ? 30 : 32;
    const bodyHeight = this.getBodyHeight();

    while (
      this.bodyText.text.length > 0 &&
      this.estimateRubyTextHeight(this.bodyText.text.length) > bodyHeight &&
      Number(this.bodyText.style.fontSize) > minFontSize
    ) {
      const nextFontSize = Number(this.bodyText.style.fontSize) - 1;
      const nextLineHeight = Math.max(minLineHeight, Number(this.bodyText.style.lineHeight) - 2);
      this.bodyText.style.fontSize = nextFontSize;
      this.bodyText.style.lineHeight = nextLineHeight;
    }
  }

  private estimateRubyTextHeight(visibleCharacterCount: number): number {
    const fontSize = Number(this.bodyText.style.fontSize);
    const lineHeight = Number(this.bodyText.style.lineHeight);
    const rubyFontSize = Math.max(RUBY_MIN_FONT_SIZE, Math.round(fontSize * RUBY_FONT_SCALE));
    const rubyLineAdvance = this.getRubyLineAdvance(lineHeight, rubyFontSize);
    const contentLineHeight = rubyFontSize + RUBY_GAP + lineHeight;
    const bodyWidth = this.boxWidth - 84;
    const tokens = this.buildRubyTokens(visibleCharacterCount);

    let lineCount = 1;
    let x = 0;

    for (const token of tokens) {
      if (token.base === '\n') {
        lineCount += 1;
        x = 0;
        continue;
      }

      const tokenWidth = this.measureRubyTokenWidth(token, fontSize, rubyFontSize);
      if (x > 0 && x + tokenWidth > bodyWidth) {
        lineCount += 1;
        x = 0;
      }

      x += tokenWidth;
    }

    return (lineCount - 1) * rubyLineAdvance + contentLineHeight;
  }

  private measureRubyTokenWidth(
    token: RubyLayoutToken,
    fontSize: number,
    rubyFontSize: number
  ): number {
    const baseText = this.createBodyFragment(token.base, fontSize, Number(this.bodyText.style.lineHeight));
    const rubyText = token.reading
      ? this.createRubyFragment(token.reading, rubyFontSize, rubyFontSize + 2)
      : null;
    const width = Math.ceil(Math.max(baseText.width, rubyText?.width ?? 0));

    baseText.destroy();
    rubyText?.destroy();
    return width;
  }

  private getRubyLineAdvance(lineHeight: number, rubyFontSize: number): number {
    return lineHeight + rubyFontSize + RUBY_GAP + RUBY_LINE_GAP;
  }

  private drawHighlights(): void {
    destroyChildren(this.highlightLayer);
    if (this.highlights.length === 0) {
      return;
    }

    const entries = this.getVisibleHighlights();
    if (entries.length === 0) {
      return;
    }

    const chipFontSize = this.compact ? 14 : 16;
    const chipWidths = entries.map((entry) => this.measureHighlightChipWidth(entry, chipFontSize));
    const totalWidth =
      chipWidths.reduce((sum, width) => sum + width, 0) + HIGHLIGHT_GAP * (chipWidths.length - 1);
    const startX = Math.max(this.getHighlightMinX(), this.boxWidth - 42 - totalWidth);
    let x = startX;

    for (const [index, entry] of entries.entries()) {
      const width = chipWidths[index];
      const chip = new Container();
      chip.position.set(Math.round(x), this.compact ? 20 : 22);

      const chipBg = new Graphics();
      chipBg
        .roundRect(0, 0, width, HIGHLIGHT_HEIGHT, 6)
        .fill({ color: UI_COLORS.gold, alpha: 0.92 })
        .stroke({ color: UI_COLORS.black, width: 1, alpha: 0.6 });
      chip.addChild(chipBg);

      const chipText = new Text({
        text: entry,
        style: {
          fill: UI_COLORS.black,
          fontFamily: UI_FONT_FAMILY,
          fontSize: chipFontSize,
          fontWeight: '900'
        }
      });
      chipText.position.set(12, Math.round((HIGHLIGHT_HEIGHT - chipText.height) / 2) - 1);
      chip.addChild(chipText);
      this.highlightLayer.addChild(chip);
      x += width + HIGHLIGHT_GAP;
    }
  }

  private getVisibleHighlights(): string[] {
    const minX = this.getHighlightMinX();
    const maxWidth = Math.max(0, this.boxWidth - minX - 42);
    const chipFontSize = this.compact ? 14 : 16;
    const entries = this.highlights.slice();

    while (entries.length > 0) {
      const widths = entries.map((entry) => this.measureHighlightChipWidth(entry, chipFontSize));
      const totalWidth =
        widths.reduce((sum, width) => sum + width, 0) + HIGHLIGHT_GAP * (widths.length - 1);
      if (totalWidth <= maxWidth) {
        return entries;
      }
      entries.pop();
    }

    return [];
  }

  private measureHighlightChipWidth(text: string, fontSize: number): number {
    const label = new Text({
      text,
      style: {
        fill: UI_COLORS.black,
        fontFamily: UI_FONT_FAMILY,
        fontSize,
        fontWeight: '900'
      }
    });
    const width = Math.max(72, Math.ceil(label.width) + 24);
    label.destroy();
    return width;
  }

  private getHighlightMinX(): number {
    if (!this.speakerText.visible) {
      return 42;
    }

    return 34 + this.getSpeakerPlateWidth() + 16;
  }

  private drawBodyMask(): void {
    this.bodyMask.clear();
    this.bodyMask
      .rect(42, this.getBodyY(), this.boxWidth - 84, this.getBodyHeight())
      .fill(0xffffff);
  }

  private updateTransition(deltaMs: number): void {
    if (!this.transition) {
      return;
    }

    this.transition.elapsedMs += deltaMs;
    const progress = Math.min(1, this.transition.elapsedMs / this.transition.durationMs);
    this.alpha = lerp(this.transition.fromAlpha, this.transition.toAlpha, easeOutCubic(progress));

    if (progress >= 1) {
      this.visible = !this.transition.hideWhenComplete;
      this.transition = null;
    }
  }

  private updateSpeakerTransition(deltaMs: number): void {
    if (this.speakerTransitionMs <= 0) {
      if (this.speakerText.visible) {
        this.speakerPlate.alpha = 1;
        this.speakerText.alpha = 1;
      }
      return;
    }

    this.speakerTransitionMs = Math.max(0, this.speakerTransitionMs - deltaMs);
    const progress = 1 - this.speakerTransitionMs / SPEAKER_TRANSITION_MS;
    const alpha = 0.72 + 0.28 * easeOutCubic(progress);
    this.speakerPlate.alpha = alpha;
    this.speakerText.alpha = alpha;
  }

  private updatePromptPulse(deltaMs: number): void {
    if (!this.promptIndicator.visible) {
      this.promptIndicator.alpha = 0;
      return;
    }

    this.promptElapsedMs = (this.promptElapsedMs + deltaMs) % PROMPT_PULSE_MS;
    const pulse = 0.5 + 0.5 * Math.sin((this.promptElapsedMs / PROMPT_PULSE_MS) * Math.PI * 2);
    this.promptIndicator.alpha = 0.58 + 0.36 * pulse;
  }

  private getSpeakerPlateWidth(): number {
    return Math.min(320, Math.max(180, this.boxWidth * 0.28));
  }

  private getBodyY(): number {
    if (this.speakerText.visible) {
      return this.compact ? 66 : 72;
    }

    return this.compact ? 32 : 38;
  }

  private getBodyHeight(): number {
    return this.boxHeight - this.getBodyY() - 24;
  }
}

function arraysEqual(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

function findRubyBaseIndex(
  reference: string,
  annotation: ScenarioRuby,
  occupiedRanges: readonly RubyRange[]
): number {
  const base = annotation.base;
  let searchFrom = 0;
  let occurrence = 0;

  while (searchFrom < reference.length) {
    const index = reference.indexOf(base, searchFrom);
    if (index < 0) {
      return -1;
    }

    searchFrom = index + base.length;
    const end = index + base.length;
    if (occupiedRanges.some((range) => index < range.end && end > range.start)) {
      continue;
    }

    occurrence += 1;
    if (annotation.occurrence !== undefined && occurrence !== annotation.occurrence) {
      continue;
    }

    return index;
  }

  return -1;
}

function destroyChildren(container: Container): void {
  for (const child of container.removeChildren()) {
    child.destroy({ children: true });
  }
}

function easeOutCubic(progress: number): number {
  return 1 - Math.pow(1 - progress, 3);
}

function lerp(from: number, to: number, progress: number): number {
  return from + (to - from) * progress;
}
